import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';
import type { TFunction } from 'i18next';

import { BotonCerrarHoja } from '#components/common/ModalHoja';

import { buscarEmoji } from './buscarEmoji';
import { SECCIONES_EMOJI } from './emojisFinanzas';
import type { IdSeccionEmoji } from './emojisFinanzas';
import { Hoja } from './Hoja';
import { Icono } from './Icono';
import { color, radio, suave, TACTIL, texto } from './tokens';

/** Lado de cada casilla de la rejilla (zona táctil ≥ 44 px). */
export const LADO_CASILLA_EMOJI = 48;

function tituloSeccion(id: IdSeccionEmoji, t: TFunction): string {
  switch (id) {
    case 'casa':
      return t('House');
    case 'comida':
      return t('Food');
    case 'transporte':
      return t('Transport');
    case 'ocio':
      return t('Leisure');
    case 'salud':
      return t('Health');
    case 'ninos':
      return t('Kids');
    case 'mascotas':
      return t('Pets');
    case 'dinero':
      return t('Money and savings');
    case 'facturas':
      return t('Bills');
    case 'compras':
      return t('Shopping');
    case 'viajes':
      return t('Travel');
    default:
      return t('Other');
  }
}

/** Primera palabra de un emoji, para su aria-label («🛒» → «supermercado»). */
const NOMBRE_EMOJI: ReadonlyMap<string, string> = (() => {
  const mapa = new Map<string, string>();
  for (const s of SECCIONES_EMOJI) {
    for (const [emoji, palabras] of s.emojis) {
      if (!mapa.has(emoji)) {
        mapa.set(emoji, palabras.split(/\s+/)[0]);
      }
    }
  }
  return mapa;
})();

type SelectorEmojiProps = {
  /** Emoji actual (sale marcado). */
  actual: string | null;
  /** `null` = «Sin emoji». */
  onElegir: (emoji: string | null) => void;
  onCerrar: () => void;
};

/**
 * Selector de emoji propio (6-oct-2026): rejilla por secciones pensadas
 * para el dinero de casa, buscador por nombre en español y «Sin emoji».
 * Casillas de 48 px. Elegir cierra la hoja (lo hace quien la abre).
 */
export function SelectorEmoji({
  actual,
  onElegir,
  onCerrar,
}: SelectorEmojiProps) {
  const { t } = useTranslation();
  const [consulta, setConsulta] = useState('');
  const buscando = consulta.trim().length > 0;
  const resultados = useMemo(() => buscarEmoji(consulta), [consulta]);

  const rejilla = (emojis: readonly string[], clave: string) => (
    <View
      key={clave}
      role="group"
      style={{
        // Dentro de la lista con scroll (columna flex): sin encoger.
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${LADO_CASILLA_EMOJI}px, 1fr))`,
        gap: 4,
      }}
    >
      {emojis.map(emoji => {
        const elegido = emoji === actual;
        return (
          <button
            key={emoji}
            type="button"
            data-testid="selector-emoji-casilla"
            aria-label={NOMBRE_EMOJI.get(emoji) ?? emoji}
            aria-pressed={elegido}
            onClick={() => onElegir(emoji)}
            className={css({
              minWidth: TACTIL,
              height: LADO_CASILLA_EMOJI,
              border: 0,
              borderRadius: radio.boton,
              padding: 0,
              fontSize: 26,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: elegido
                ? suave(color.accent, 18)
                : 'transparent',
              boxShadow: elegido ? `inset 0 0 0 2px ${color.accent}` : 'none',
              '&:active': { backgroundColor: color.surface2 },
            })}
          >
            {emoji}
          </button>
        );
      })}
    </View>
  );

  return (
    <Hoja
      aria-label={t('Choose an emoji')}
      data-testid="selector-emoji"
      onCerrar={onCerrar}
      alto="90%"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 10,
          flexShrink: 0,
        }}
      >
        <Text style={{ ...texto.titulo, flex: 1, color: color.fg }}>
          <Trans>Choose an emoji</Trans>
        </Text>
        <BotonCerrarHoja onPress={onCerrar} />
      </View>

      <label
        className={css({
          height: 48,
          borderRadius: radio.boton,
          backgroundColor: color.surface2,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 14px',
          border: '2px solid transparent',
          flexShrink: 0,
          marginBottom: 8,
          '&:focus-within': { borderColor: color.accent },
        })}
      >
        <Icono nombre="search" size={18} style={{ color: color.fg2 }} />
        <input
          type="text"
          inputMode="search"
          autoComplete="off"
          data-testid="selector-emoji-buscar"
          aria-label={t('Search emoji')}
          placeholder={t('Search emoji')}
          value={consulta}
          onChange={e => setConsulta(e.target.value)}
          className={css({
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            color: color.fg,
            fontFamily: 'inherit',
            fontSize: 15.5,
            fontWeight: 700,
            '&::placeholder': { color: color.fg3, fontWeight: 600 },
          })}
        />
      </label>

      <button
        type="button"
        data-testid="selector-emoji-ninguno"
        aria-pressed={actual === null}
        onClick={() => onElegir(null)}
        className={css({
          minHeight: TACTIL,
          border: `1px solid ${color.line2}`,
          borderRadius: radio.boton,
          backgroundColor: 'transparent',
          color: color.fg,
          fontFamily: 'inherit',
          fontSize: 15,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          cursor: 'pointer',
          flexShrink: 0,
          marginBottom: 8,
        })}
      >
        <Icono nombre="x" size={16} />
        <Trans>No emoji</Trans>
      </button>

      <View
        data-testid="selector-emoji-lista"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
        }}
      >
        {buscando ? (
          resultados.length > 0 ? (
            rejilla(resultados, 'resultados')
          ) : (
            <Text
              style={{
                padding: '12px 4px',
                fontSize: 13,
                fontWeight: 600,
                color: color.fg2,
              }}
            >
              <Trans>No results</Trans>
            </Text>
          )
        ) : (
          SECCIONES_EMOJI.map(seccion => (
            <View key={seccion.id} style={{ marginBottom: 10, flexShrink: 0 }}>
              <Text
                role="heading"
                aria-level={3}
                style={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: color.fg3,
                  padding: '6px 4px',
                }}
              >
                {tituloSeccion(seccion.id, t)}
              </Text>
              {rejilla(
                seccion.emojis.map(([e]) => e),
                seccion.id,
              )}
            </View>
          ))
        )}
      </View>
    </Hoja>
  );
}
